import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isPaymentInReview, paymentInReviewMessage, paymentErrorMessage } from '../paymentErrors';
import axios from 'axios';
import Select from 'react-select';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHourglassHalf, faInfoCircle, faCheckCircle, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { coralSelectStyles, coralSelectTheme } from './coralSelect';
import { resolvePayNow } from '../payNow';
import WalletShortfallPanel from '../WalletShortfallPanel';
import { readWalletFloor } from '../walletShortfall';
import ClientDepositForm from '../ClientWallet/ClientDepositForm';
import SavedCardPicker, { cardCheckoutBody } from '../ClientWallet/SavedCardPicker';
import HostedCardPanel from '../payments/HostedCardPanel';
import { CARD_CONSENT_TEXT } from '../ClientWallet/cardConsent';
import SavedCardPay from '../payments/SavedCardPay';
import TaskListEditor from './TaskListEditor';
import BookingDateStrip from './BookingDateStrip';
import RequestBookingCancelModal from './RequestBookingCancelModal';
import { saveBookingEditDraft, clearBookingEditDraft, loadBookingEditDraft } from './bookingEditDraft';
import { setTopupBookingIntent } from './topupBookingIntent';
import useBfcacheReset from '../useBfcacheReset';
import './clientRequests.css';

const HOURLY_TYPE_IDS = [2, 4];
// Only All Inclusive is client-bookable now; an existing hourly booking still
// shows its own type read-only via the fallback label below.
const SERVICE_TYPES = [
    { id: 1, name: 'All Inclusive', desc: 'Full cleaning with linen & amenities at your fixed property price' },
];
const DURATION_OPTIONS = [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8]
    .map((h) => ({ value: h, label: `${h} Hour${h === 1 ? '' : 's'}` }));

// Linen-hire inclusion window — same rule/copy as the create page.
const LINEN_HIRE_TYPES = [1, 5];
const LINEN_HIRE_NOTE = 'Bookings include linen hire for up to 2 weeks. Linens kept longer are chargeable, billed every 2 weeks.';

// Same selectable slots as the create page (15:00/15:30 excluded — the
// pre-off-peak confusion window).
const EXCLUDED_SLOTS = ['15:00', '15:30'];
const ALL_TIME_SLOTS = [];
for (let h = 10; h <= 16; h++) {
    for (const m of ['00', '30']) {
        const slot = `${String(h).padStart(2, '0')}:${m}`;
        if (!EXCLUDED_SLOTS.includes(slot)) ALL_TIME_SLOTS.push(slot);
    }
}
ALL_TIME_SLOTS.push('17:00');
const OFFPEAK_FROM = '16:00';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function parseLocalDate(ymd) {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
}
// ITEM-8 EXCEPTION (deliberate — do NOT route through dubaiTime): this labels
// a calendar day the client just PICKED in the date strip, not a stored
// instant. Local-midnight Date in, same local fields out — device-safe as is;
// a Dubai conversion would shift the label a day for users west of UTC.
function readableWhen(ymd, time) {
    const picked = parseLocalDate(ymd);
    if (!picked) return '';
    const label = picked.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return time ? `${label} at ${time}` : label;
}

// Client "Edit Booking" PAGE (B1, owner 2026-07-19) — the former
// RequestBookingEditModal re-homed at /client/bookings/:bookingId/edit-request
// so the paypage/top-up round-trips and deep links land somewhere real and
// the browser Back button behaves. Only date/time, cleaners + duration
// (hourly types) and notes are editable — service type and property are fixed
// (cancel + rebook to change those). Submits a SPARSE edit request: only the
// fields that actually changed.
//
// R3 (PLAN_pending_settlement_requirements.md): the price impact comes from
// the server's edit-quote — the EXACT delta approval charges (new estimate +
// unchanged extras − TRUE net-charged from the raw wallet ledger, card
// round-trips included). When the delta is an INCREASE the client picks how
// to cover it — use wallet, or pay by card now — same UX as a new booking
// request; the card path funds the difference up front via
// /api/payments/edit-request-checkout so approval never raises an adjustment.
//
// URL contract: ?resumeDraft=1 prefills from the persisted snapshot
// (bookingEditDraft — localStorage, so it survives a killed tab or crash)
// taken right before a card redirect (the mid-edit wallet top-up, or this
// page's own card checkout). The booking is still re-fetched from the server —
// the draft only re-applies the fields the client had touched, so a booking
// that moved server-side in the meantime still shows its true values.
export default function RequestBookingEditPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const bookingId = Number(params.bookingId);
    const [searchParams, setSearchParams] = useSearchParams();

    // Captured once: the draft belongs to THIS booking only. A stale draft for
    // a different booking must never bleed its date/time into this form.
    const draftRef = useRef((() => {
        if (searchParams.get('resumeDraft') !== '1') return null;
        const d = loadBookingEditDraft();
        return d && Number(d.booking_id) === Number(bookingId) ? d : null;
    })());
    // Tasks are loaded by their own request, so the draft's task list is
    // consumed by THAT effect — applying it from the booking effect would let
    // whichever response landed second overwrite the client's edits.
    const draftTasksRef = useRef(Array.isArray(draftRef.current?.tasks) ? draftRef.current.tasks : null);

    // The resume param has done its job — strip it so refresh/share is the
    // plain edit page (the draft stays in storage until submitted).
    useEffect(() => {
        if (searchParams.get('resumeDraft')) {
            const next = new URLSearchParams(searchParams);
            next.delete('resumeDraft');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Leaving without submitting: back to wherever the client came from
    // (bookings list or the booking's detail page), or to My Bookings on a
    // deep link with no in-app history.
    const exitToBookings = () => {
        if (location.key && location.key !== 'default') navigate(-1);
        else navigate('/client/bookings');
    };

    const [original, setOriginal] = useState(null);
    const [loadError, setLoadError] = useState('');
    // Set when the booking can't be edited online (invoiced / non-editable type)
    const [blockedReason, setBlockedReason] = useState('');
    const [form, setForm] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    // The edit-quote endpoint enforces client slot rules on a CHANGED schedule;
    // an admin-created booking can sit outside them (e.g. 09:00). If the preview
    // fails we let the submit through — the server re-quotes and gates
    // authoritatively (wallet path only; card needs a sized quote).
    const [quoteFailed, setQuoteFailed] = useState(false);
    // The server's actual reason (a 4xx validation message), surfaced instead of
    // the vague fallback so the client — and we — can see WHY the preview failed
    // (e.g. "This booking isn't paid yet…"), not just that it did.
    const [quoteFailReason, setQuoteFailReason] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Calm, persistent money notice (PAYMENT_IN_REVIEW — the gateway is still
    // holding an earlier approved payment for this intent). Not an error: there
    // is nothing for the client to fix or retry, only to wait for.
    const [payNotice, setPayNotice] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    // Last-minute tick box for a RESCHEDULE into a last-minute slot, and the
    // notice the server sent back when a submit was refused for want of it.
    const [lmAck, setLmAck] = useState(false);
    const [serverNotice, setServerNotice] = useState(null);
    const [showDeposit, setShowDeposit] = useState(false);
    // "Cancel this booking instead" — the cancel flow stays a modal; it is
    // hosted here so the page keeps the same escape hatch the modal had.
    const [showCancelModal, setShowCancelModal] = useState(false);
    // Once a cancellation is SUBMITTED, this edit form is stale (the booking
    // now has a pending cancel request) — closing the modal's done screen
    // leaves for My Bookings instead of returning to a doomed form.
    const cancelSubmittedRef = useRef(false);
    // Shortfall from a server-side INSUFFICIENT_BALANCE rejection (reachable via
    // the quoteFailed escape hatch, where no client-side quote exists).
    const [serverShortfall, setServerShortfall] = useState(0);
    // ZERO FLOOR (P3): the server's structured refusal when this change would
    // take AVAILABLE below zero. A block, not a failure — see WalletShortfallPanel.
    const [walletFloor, setWalletFloor] = useState(null);
    // Settlement selector (R3): how an INCREASE is covered.
    // CHECKOUT HIERARCHY (item 7, 2026-07-19): this surface has NO
    // auto-charge rail (there is no edit-request-autocharge endpoint — an
    // increase is settled now, by wallet and/or card), so the shared decider
    // (autoPay.defaultPayMode) collapses to its rule 1 here: wallet-first Pay
    // now, toggle ON by default whenever anything is available. That is
    // exactly this default; resolvePayNow ignores the toggle when the wallet
    // holds nothing, and re-derives the rail each render as the quote loads.
    // Unticking is a manual choice and nothing programmatic reverts it (the
    // shortfall panel's explicit "Pay by card" action is the client's own
    // click).
    const [useWalletBalance, setUseWalletBalance] = useState(true);
    const [savedCards, setSavedCards] = useState([]);
    const [cardSelection, setCardSelection] = useState('new'); // card id | 'new'
    // EMBEDDED CARD RAILS (2026-08-16) — the same two the new-booking page uses:
    // a stored card is paid here, with its code in the picker row and its Pay
    // button in this page's action row; a new card (or Apple Pay) gets a second
    // step holding only the payment. hostedRail 'off' = the SDK could not mount,
    // and the paypage redirect underneath is used unchanged.
    const [payStep, setPayStep] = useState('form');
    const [hostedRail, setHostedRail] = useState('probe');
    const [savedCvv, setSavedCvv] = useState('');
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    const [paySlot, setPaySlot] = useState(null);
    const [saveNewCard, setSaveNewCard] = useState(false);
    const [cardPaid, setCardPaid] = useState(null);
    // The order staged by an attempt that has NOT been paid. A card checkout
    // stages the request server-side as 'awaiting_payment', so walking away from
    // the card form used to leave a request that could be neither paid nor
    // withdrawn — the booking simply said "change requested" forever. Leaving
    // the step releases it.
    const stagedOrderRef = useRef(null);
    // Card attempt came back WALLET_COVERS_TOTAL → fell back to wallet submit.
    const [walletCoveredNote, setWalletCoveredNote] = useState(false);

    // Ad-hoc booking tasks (source='booking') — editable one-off tasks.
    const [tasks, setTasks] = useState([]);
    const [tasksOriginal, setTasksOriginal] = useState([]);

    // Extra linens the client may add/change on this booking.
    const [clientAddons, setClientAddons] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/api/client-requests/requests/bookings/client-addons');
                if (!cancelled) setClientAddons(Array.isArray(res.data?.addons) ? res.data.addons : []);
            } catch { if (!cancelled) setClientAddons([]); }
        })();
        return () => { cancelled = true; };
    }, []);
    const addonQty = (itemId) =>
        (form?.selected_addons || []).find((a) => Number(a.item_id) === Number(itemId))?.quantity || 0;
    const setAddonQty = (itemId, qty) => {
        setForm((prev) => {
            const others = (prev.selected_addons || []).filter((a) => Number(a.item_id) !== Number(itemId));
            const q = Math.max(0, Math.min(20, Math.round(qty)));
            return { ...prev, selected_addons: q > 0 ? [...others, { item_id: Number(itemId), quantity: q }] : others };
        });
    };
    useEffect(() => {
        let stale = false;
        axios.get(`/api/client-requests/bookings/${bookingId}/tasks`)
            .then((res) => {
                if (stale) return;
                const rows = (Array.isArray(res.data) ? res.data : [])
                    .filter((t) => t.source === 'booking')
                    .map((t) => ({ id: t.id, instruction: t.instruction, is_report_required: !!t.is_report_required }));
                // tasksOriginal is always the SERVER rows — it is the baseline
                // `tasksChanged` diffs against, so a resumed draft must not
                // become its own baseline or the edit would look unchanged.
                setTasksOriginal(rows);
                if (Array.isArray(draftTasksRef.current)) {
                    setTasks(draftTasksRef.current);
                    draftTasksRef.current = null; // one-shot
                } else {
                    setTasks(rows);
                }
            })
            .catch(() => { if (!stale) { setTasks([]); setTasksOriginal([]); } });
        return () => { stale = true; };
    }, [bookingId]);
    const cleanTasks = (arr) => arr
        .map((t) => ({ id: t.id ?? null, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
        .filter((t) => t.instruction);
    const tasksChanged = JSON.stringify(cleanTasks(tasks)) !== JSON.stringify(cleanTasks(tasksOriginal));

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    // Saved cards (best-effort — the page must work even if the cards endpoint
    // isn't deployed). Default the card picker to the most recent saved card.
    useEffect(() => {
        let stale = false;
        axios.get('/api/cards')
            .then((res) => {
                if (stale) return;
                const list = Array.isArray(res.data?.cards) ? res.data.cards : [];
                setSavedCards(list);
                if (list.length > 0) setCardSelection(list[0].id);
            })
            .catch((err) => {
                if (!stale && err.response?.status !== 404) console.error('Error loading saved cards:', err);
            });
        return () => { stale = true; };
    }, []);

    // Load + parse the live booking for prefill. booking_start round-trips the
    // same way ClientBookings renders it (UTC-marked ISO → Dubai wall time).
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`/api/client-requests/bookings/${bookingId}`);
                const b = res.data;

                // Mirror the server guardrails that the list view can't see, so the
                // client gets a friendly explanation instead of a doomed form.
                if (b.invoice_id != null) {
                    setBlockedReason('This booking has already been invoiced, so it can’t be changed online — please contact us and we’ll arrange it.');
                    return;
                }
                if (![1, 2, 4].includes(b.type_id)) {
                    setBlockedReason('This booking type can’t be changed online — please contact us and we’ll arrange it.');
                    return;
                }

                const dt = DateTime.fromISO(b.booking_start, { zone: 'utc' }).setZone('Asia/Dubai');
                const orig = {
                    booking_id: b.booking_id,
                    property_id: b.property_id,
                    property_label: `Unit ${b.unit_number} · ${b.building_name}`,
                    type_id: b.type_id,
                    type_label: b.booking_type,
                    date: dt.isValid ? dt.toFormat('yyyy-MM-dd') : '',
                    time: dt.isValid ? dt.toFormat('HH:mm') : '',
                    required_cleaners: b.required_cleaners ?? 1,
                    duration_hours: Number(b.duration_hours) || 2,
                    notes: b.booking_specific_notes || '',
                    current_total: Number(b.total_with_vat) || 0,
                    lm_window: b.lm_window === true,
                    lm_fee_incl_vat: Number(b.lm_fee_incl_vat) || 0,
                    // The booking's current extra linens, so the picker prefills
                    // and we can diff for a change.
                    selected_addons: (b.current_extra_linens || []).map((l) => ({ item_id: Number(l.item_id), quantity: Number(l.quantity) })),
                };
                setOriginal(orig);
                setForm({
                    type_id: orig.type_id,
                    property_id: orig.property_id,
                    date: orig.date,
                    time: orig.time,
                    required_cleaners: orig.required_cleaners,
                    duration_hours: orig.duration_hours,
                    notes: orig.notes,
                    selected_addons: orig.selected_addons,
                    // Resumed from a card top-up round-trip: the client's own
                    // edits win over the server values they were editing.
                    ...(draftRef.current?.form || {}),
                });
                draftRef.current = null; // one-shot — a later refetch starts clean
            } catch (err) {
                console.error('Error loading booking for edit:', err);
                setLoadError('Could not load this booking. Please try again.');
            }
        })();
    }, [bookingId]);

    // Rules follow the SELECTED type (like the create form)
    const isHourly = form ? HOURLY_TYPE_IDS.includes(form.type_id) : false;

    // Restored from the paypage's Back button (bfcache) → clear the frozen
    // "Submitting…" state so the page is usable again.
    useBfcacheReset(() => setSubmitting(false));

    // Dubai wall-clock, with the same 1.5-hour lead time the server enforces
    // (BOOKING_TOO_SOON) — a client browsing from another timezone must see
    // the same availability.
    const MIN_LEAD_MINUTES = 90;
    const isSlotTooSoon = (t) => {
        if (!form?.date) return false;
        const when = DateTime.fromISO(`${form.date}T${t}`, { zone: 'Asia/Dubai' });
        return when.isValid
            ? when < DateTime.now().setZone('Asia/Dubai').plus({ minutes: MIN_LEAD_MINUTES })
            : false;
    };

    // Clear a chosen time that becomes past ONLY when the client actively picks
    // a DIFFERENT date than the booking's own (a real reschedule). It must NOT
    // fire on initial load, nor while sitting on the original date: a same-day
    // booking's own time is legitimately within the lead window, and wiping it
    // here marked the schedule "changed" and blocked a task-only / notes-only
    // edit from submitting (2026-07-20).
    useEffect(() => {
        if (!original) return;
        if (form?.date && form.date !== original.date && form?.time && isSlotTooSoon(form.time)) {
            set({ time: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form?.date]);

    // What changed vs. the original booking?
    const changes = useMemo(() => {
        if (!original || !form) return {};
        const norm = (v) => (v == null ? '' : String(v).trim());
        const addonKey = (arr) => (arr || []).map((a) => `${a.item_id}:${a.quantity}`).sort().join(',');
        return {
            type: Number(form.type_id) !== Number(original.type_id),
            schedule: form.date !== original.date || form.time !== original.time,
            cleaners: isHourly && Number(form.required_cleaners) !== Number(original.required_cleaners),
            duration: isHourly && Number(form.duration_hours) !== Number(original.duration_hours),
            notes: norm(form.notes) !== norm(original.notes),
            linens: addonKey(form.selected_addons) !== addonKey(original.selected_addons),
        };
    }, [original, form, isHourly]);
    const hasChanges = Object.values(changes).some(Boolean) || tasksChanged;
    // Extra linens affect the price, so a linen change re-quotes the delta too.
    const priceTouched = changes.type || changes.schedule || changes.cleaners || changes.duration || changes.linens;

    const wallTimeStart = form?.date && form?.time ? `${form.date} ${form.time}:00` : '';

    // The SPARSE edit body — only fields that actually changed. The quote, the
    // wallet submit and the card checkout all send it, so the delta the client
    // saw, the delta the request stores, and the delta approval charges are the
    // same computation (linked_deposit_id / payment fields ride on top).
    const buildEditBody = (priceFieldsOnly = false) => {
        const body = {};
        if (changes.type) body.type_id = form.type_id;
        if (changes.schedule) body.booking_start = wallTimeStart;
        if (changes.cleaners) body.required_cleaners = form.required_cleaners;
        if (changes.duration) body.duration_hours = form.duration_hours;
        // Extra linens are price-affecting → sent in the quote body too.
        if (changes.linens) body.selected_addons = form.selected_addons;
        if (!priceFieldsOnly) {
            if (changes.notes) body.notes = form.notes;
            if (tasksChanged) body.tasks = cleanTasks(tasks);
            // Booking notice: this build shows the last-minute tick box, and
            // whether the client ticked it (the server enforces and records it).
            body.notice_version = 1;
            body.last_minute_ack = lmAck === true;
        }
        return body;
    };

    // Live server quote when a price-affecting field changes (debounced) — the
    // authoritative delta (TRUE net-charged basis) + wallet context.
    useEffect(() => {
        if (!original || !priceTouched || !wallTimeStart) {
            setQuote(null);
            setQuoteLoading(false);
            setQuoteFailed(false);
            setQuoteFailReason(null);
            return undefined;
        }
        setQuoteLoading(true);
        setQuoteFailed(false);
        let stale = false; // out-of-order guard: only the latest request may land
        const handle = setTimeout(async () => {
            try {
                const res = await axios.post(
                    `/api/client-requests/requests/bookings/${bookingId}/edit-quote`,
                    buildEditBody(true)
                );
                if (!stale) setQuote(res.data);
            } catch (err) {
                if (!stale) {
                    setQuote(null);
                    setQuoteFailed(true);
                    // A 4xx carries a real reason worth showing; 5xx / network
                    // errors keep the calm generic fallback.
                    const status = err?.response?.status;
                    const reason = err?.response?.data?.error;
                    setQuoteFailReason(status >= 400 && status < 500 && reason ? reason : null);
                }
            } finally {
                if (!stale) setQuoteLoading(false);
            }
        }, 350);
        return () => { stale = true; clearTimeout(handle); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [original, priceTouched, wallTimeStart, form?.type_id, form?.property_id, form?.required_cleaners, form?.duration_hours, form?.selected_addons, bookingId]);

    // Moving the start of a booking that begins within 24h = LAST-MINUTE
    // reschedule (server-confirmed once the quote lands; local heuristic while
    // it loads): the original visit keeps the LM fee and a NEW booking is
    // created at the requested time.
    const isLmReschedule = quote
        ? !!quote.is_last_minute_reschedule
        : !!(original?.lm_window && changes.schedule);

    // The single number that hits the wallet if this change is approved —
    // server-computed: estimate(new) + unchanged extras − TRUE net-charged
    // (or the LM variant). Null until the quote lands.
    const effectiveDelta = quote ? Math.round(Number(quote.price_delta) * 100) / 100 : null;
    // An INCREASE needs a settlement choice (R3) — decreases/no-change don't.
    const needsSettlement = effectiveDelta != null && effectiveDelta > 0.01;

    // `quote.available` IS the canonical available_balance for this edit (the
    // server computes it with this request's own delta excluded). The
    // wallet.total fallback fires only on a server that predates the field
    // entirely — the same last-resort rule as walletFigures.availableBalance,
    // never as a "nicer number".
    const availableRaw = quote ? Number(quote.available ?? quote.wallet?.total ?? 0) : 0;

    // What the wallet looks like AFTER approval (available already nets the
    // client's other pending requests). Wallet-settled figure.
    const balanceAfterChange = quote && effectiveDelta != null
        ? Math.round((availableRaw - effectiveDelta) * 100) / 100
        : null;

    // Wallet-mode gate context (same fields the server gate uses).
    const coveredByPending = quote ? !!quote.covered_by_pending_deposits : false;
    const rawShortfall = quote && needsSettlement ? Math.max(0, Number(quote.shortfall_after_pending || 0)) : 0;
    // Rounded up to the next AED 100 — same as the create flow's in-place top-up.
    const shortfall = Math.ceil(rawShortfall / 100) * 100;

    // ONE derived plan (DISPLAY ONLY — the server recomputes authoritatively
    // from the same edit body). availableRaw may legitimately be negative (P3);
    // resolvePayNow floors it for the contribution maths without hiding it.
    const payPlan = resolvePayNow({
        amount: needsSettlement ? effectiveDelta : 0,
        spendable: availableRaw,
        useWallet: useWalletBalance,
    });
    const applyWallet = payPlan.walletPortion > 0.005;
    const walletPortion = payPlan.walletPortion;
    const cardPortion = payPlan.cardPortion;
    // WHICH EMBEDDED RAIL IS ARMED — see the state block above.
    const embeddedCardRail = hostedRail !== 'off';
    const selectedSavedCard = savedCards.find((c) => String(c.id) === String(cardSelection)) || null;
    const savedCardActive = !!selectedSavedCard && cardPortion > 0 && !coveredByPending && needsSettlement;
    const embeddedPayOwnsButton = payStep === 'card' || savedCardActive;
    // If the schedule is being changed, the new time must be a valid client slot —
    // an off-slot original (admin-created, e.g. 09:00) can't ride along unchanged
    // into a new date; the server would reject it.
    const timeValidForChange = !changes.schedule || ALL_TIME_SLOTS.includes(form?.time || '');

    // A valid date+time is required ONLY when the client is actually changing the
    // schedule. A task-only or notes-only edit keeps the original schedule and
    // must be submittable without re-picking a time (2026-07-20).
    const scheduleOk = changes.schedule
        ? (!!form?.date && !!form?.time && timeValidForChange)
        : true;
    // LAST-MINUTE / OUTSIDE-OFFICE-HOURS NOTICE for the NEW start — only when
    // the start is being moved. Decided by the server on the Dubai clock
    // (routes/services/bookingNotice.js) and returned with the edit quote,
    // wording included; `serverNotice` is the same object arriving with a
    // refused submit (the quote had failed, or the page sat open across 8 pm
    // or midnight). Every submit on this page is gated on canSubmit and sends
    // buildEditBody(), so the one flag covers wallet and card alike.
    const notice = changes.schedule ? (serverNotice || quote?.notice || null) : null;
    const ackRequired = !!notice?.ack_required;
    useEffect(() => { setLmAck(false); setServerNotice(null); }, [wallTimeStart]);

    const adoptServerNotice = (err) => {
        const data = err?.response?.data;
        if (data?.code !== 'LAST_MINUTE_ACK_REQUIRED') return false;
        if (data.notice) setServerNotice(data.notice);
        setLmAck(false);
        return true;
    };

    const canSubmit = hasChanges && !submitting
        && scheduleOk
        && (!priceTouched || quoteFailed || (quote && !quoteLoading))
        && (!ackRequired || lmAck);

    const submit = async (linkedDepositId = null) => {
        setSubmitting(true);
        setError('');
        try {
            const body = buildEditBody();
            if (linkedDepositId) body.linked_deposit_id = linkedDepositId;
            await axios.post(`/api/client-requests/requests/bookings/${bookingId}/edit`, body);
            clearBookingEditDraft(); // submitted — the belt-and-braces draft is done
            setDone(true);
        } catch (err) {
            console.error('Error submitting change request:', err);
            const floor = readWalletFloor(err);
            if (floor) {
                // ZERO FLOOR (P3). Available would go below zero. Nothing was
                // charged, so this renders as the shortfall panel with card /
                // top-up / reduce actions — not as red error text, and not by
                // force-opening the top-up form as if that were the only way out.
                setWalletFloor(floor);
                if (floor.shortfall != null && floor.shortfall > 0) {
                    setServerShortfall(Math.max(100, Math.ceil(floor.shortfall / 100) * 100));
                }
                setError('');
            } else if (err.response?.data?.code === 'BOOKING_TOO_SOON') {
                setError('That start time is less than 1.5 hours away (Dubai time) — please pick a later start time.');
            } else if (adoptServerNotice(err)) {
                setError(err.response.data.message || err.response.data.error || '');
            } else if (err.response?.data?.code === 'CLIENT_EDIT_NOT_ALLOWED'
                || err.response?.data?.code === 'EDIT_NOT_ALLOWED') {
                // The UI normally hides Edit for these bookings
                // (can_client_edit === false), but the flag can go stale between
                // page load and submit — so the refusal lands as a plain
                // explanation, never a red "Failed to submit".
                setError(err.response.data.error
                    || 'This booking can only be changed by our team — please contact us and we’ll sort it out.');
            } else {
                setError(paymentErrorMessage(err, 'Failed to submit the change request.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Pay the increase by card now (R3): create the edit request + hosted
    // checkout in one call, then hand the browser to the paypage.
    // The payment-return page handles the return; the request is withdrawn
    // automatically if the payment fails/expires (same as new bookings).
    const submitCardCheckout = async () => {
        setSubmitting(true);
        setError('');
        // Belt-and-braces (item 4): snapshot the half-made change BEFORE every
        // paypage handoff — cancelling the payment (or a killed tab) must
        // always find a draft to come back to ("Edit & resubmit").
        // Cleared on successful submit / paid checkout; re-saving is harmless
        // (a single overwritten key).
        saveBookingEditDraft({ bookingId, form, tasks });
        try {
            const res = await axios.post('/api/payments/edit-request-checkout', {
                ...buildEditBody(),
                booking_id: bookingId,
                use_wallet: applyWallet,
                // One-off card payment only — saving a card lives in the wallet
                // page flows (saveNew always false here).
                ...cardCheckoutBody(cardSelection, false),
            });
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // keep `submitting` on — we are navigating away
            }
            setError('Could not open the secure payment page. Please try again.');
            setSubmitting(false);
        } catch (err) {
            if (['WALLET_COVERS_TOTAL', 'NOTHING_TO_PAY'].includes(err.response?.data?.code)) {
                // The wallet already covers the whole difference — no card
                // payment to make. Fall back to the normal wallet submit.
                setWalletCoveredNote(true);
                await submit();
                return;
            }
            if (err.response?.data?.code === 'BOOKING_TOO_SOON') {
                setError('That start time is less than 1.5 hours away (Dubai time) — please pick a later start time.');
                setSubmitting(false);
                return;
            }
            if (adoptServerNotice(err)) {
                setError(err.response.data.message || err.response.data.error || '');
                setSubmitting(false);
                return;
            }
            if (isPaymentInReview(err)) {
                // An earlier card payment for this change is still being held by
                // the gateway — starting a second one could double-charge.
                setPayNotice(paymentInReviewMessage(err));
                setError('');
                setSubmitting(false);
                return;
            }
            console.error('Error starting card checkout:', err);
            setError(paymentErrorMessage(err, 'Could not start the card payment.'));
            setSubmitting(false);
        }
    };

    // ── EMBEDDED CARD RAILS ────────────────────────────────────────────────
    // Same endpoint and same body as the redirect rail above, plus one field:
    // the SDK's session, or the stored card's code. The server prices the delta,
    // stages the edit request and captures exactly as before.

    const handleEditCheckoutError = async (err) => {
        if (['WALLET_COVERS_TOTAL', 'NOTHING_TO_PAY'].includes(err.response?.data?.code)) {
            setWalletCoveredNote(true);
            setPayStep('form');
            await submit();
            return true;
        }
        if (err.response?.data?.code === 'BOOKING_TOO_SOON') {
            setPayStep('form');
            setError('That start time is less than 1.5 hours away (Dubai time) — please pick a later start time.');
            return true;
        }
        if (adoptServerNotice(err)) {
            // Stay on this step (a typed card is not thrown away) — the tick
            // box sits above the action row on every step.
            setError(err.response.data.message || err.response.data.error || '');
            return true;
        }
        if (isPaymentInReview(err)) {
            setPayNotice(paymentInReviewMessage(err));
            setError('');
            return true;
        }
        return false;
    };

    const startHostedPayment = async (sessionId) => {
        setError('');
        saveBookingEditDraft({ bookingId, form, tasks });
        try {
            const res = await axios.post('/api/payments/edit-request-checkout', {
                ...buildEditBody(),
                booking_id: bookingId,
                use_wallet: applyWallet,
                session_id: sessionId,
                ...(saveNewCard ? { save_card: true, save_card_consent: true } : {}),
            });
            if (!res.data?.payment_response) {
                setError('Could not start the card payment. Please try again.');
                return null;
            }
            stagedOrderRef.current = res.data.order_ref || null;
            return res.data;
        } catch (err) {
            if (!(await handleEditCheckoutError(err))) {
                console.error('Error starting embedded card payment:', err);
                setError(paymentErrorMessage(err, 'Could not start the card payment.'));
            }
            return null;
        }
    };


    // Leaving the page with an unpaid attempt behind releases it too — a closed
    // tab is the same intent as Back, and sendBeacon survives the unload.
    useEffect(() => {
        const release = () => {
            const ref = stagedOrderRef.current;
            if (!ref) return;
            stagedOrderRef.current = null;
            try {
                const blob = new Blob([JSON.stringify({ order_ref: ref })], { type: 'application/json' });
                navigator.sendBeacon?.('/api/payments/cancel', blob);
            } catch { /* the sweep expires it */ }
        };
        window.addEventListener('pagehide', release);
        return () => {
            window.removeEventListener('pagehide', release);
            release();
        };
    }, []);

    // RELEASE AN UNPAID ATTEMPT. Cancelling the checkout tears down the request
    // it staged (and any pending deposit behind it) through the same path the
    // client cancel endpoint uses, so nothing is left waiting on a payment that
    // is never coming. Refused by the server while money is actually in flight —
    // that call is its own — and best-effort otherwise: the checkout sweep is
    // still the backstop.
    const releaseStagedCheckout = async () => {
        const ref = stagedOrderRef.current;
        if (!ref) return;
        stagedOrderRef.current = null;
        try {
            await axios.post('/api/payments/cancel', { order_ref: ref });
        } catch { /* the sweep expires it */ }
    };

    const chargeSavedCardNow = async (cvv) => {
        setError('');
        saveBookingEditDraft({ bookingId, form, tasks });
        try {
            const res = await axios.post('/api/payments/edit-request-checkout', {
                ...buildEditBody(),
                booking_id: bookingId,
                use_wallet: applyWallet,
                ...cardCheckoutBody(cardSelection, false),
                cvv,
            });
            return res.data;
        } catch (err) {
            // Nothing was charged. Bring up the tick box; the card row states
            // the outcome itself, as it does for every other refusal.
            adoptServerNotice(err);
            throw err;
        }
    };

    // Verified paid: confirmCheckout has already committed the edit request, so
    // this is the confirmation of what was charged.
    const onCardPaid = () => {
        stagedOrderRef.current = null;
        clearBookingEditDraft();
        setPayStep('form');
        setCardPaid({ total: cardPortion });
        setDone(true);
    };

    // Insufficient balance → open the deposit form first (same flow as new
    // bookings); the change request submits linked to that deposit.
    const handleSubmitClick = () => {
        if (!canSubmit) return;
        // A fresh attempt clears the previous floor block.
        setWalletFloor(null);
        // The rail is derived, not chosen: anything left after the wallet leg
        // goes to card; a fully-covered delta (or one a pending top-up will
        // cover) simply submits against the wallet.
        if (needsSettlement && payPlan.needsCard && !coveredByPending) {
            // A stored card never reaches here — its own Pay button (portalled
            // into the action row) replaces the primary. A new card goes to the
            // card step; only a browser the SDK cannot serve still redirects.
            if (embeddedCardRail) {
                setError('');
                setPayStep('card');
                return;
            }
            submitCardCheckout();
            return;
        }
        submit();
    };
    const handleDepositSuccess = (deposit) => {
        setShowDeposit(false);
        clearBookingEditDraft(); // the top-up is done and we are submitting now
        submit(deposit?.id || null);
    };

    // Opening the top-up form snapshots the half-made change FIRST — the client
    // may now pay for that top-up by card, which hands the browser to the hosted
    // paypage with no unmount hook to save anything on the way out. Snapshotting
    // here (rather than at the redirect, which happens inside ClientDepositForm)
    // means the draft exists whichever rail or exit they take. Re-saving is
    // harmless: it is a single overwritten key.
    const openDeposit = () => {
        saveBookingEditDraft({ bookingId, form, tasks });
        // Mark that THIS top-up came from the edit form (see topupBookingIntent).
        setTopupBookingIntent('edit');
        setShowDeposit(true);
    };

    return (
        <div className="creq-pageWrap">
            <div className="creq-modal creq-modal--wide creq-modal--page">
                {done ? (
                    <div className="creq-success">
                        <div className="creq-pending-badge">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                        </div>
                        <span className="creq-pending-chip">Pending review</span>
                        <h3>Change requested</h3>
                        <p>
                            Your booking stays as originally scheduled until our team approves the
                            change — we&apos;ll notify you the moment it&apos;s reviewed.
                        </p>
                        {(notice?.messages?.last_minute || notice?.messages?.office_hours) && (
                            <div className="creq-notice creq-notice--done" role="note">
                                {notice.messages.last_minute && (
                                    <p className="creq-notice__line creq-notice__line--lm">{notice.messages.last_minute}</p>
                                )}
                                {notice.messages.office_hours && (
                                    <p className="creq-notice__line">{notice.messages.office_hours}</p>
                                )}
                            </div>
                        )}
                        {cardPaid && (
                            <div className="creq-paid">
                                <div className="creq-paid__total">
                                    <span>Paid by card</span>
                                    <strong>{money(cardPaid.total)}</strong>
                                </div>
                            </div>
                        )}
                        {walletCoveredNote && (
                            <p className="creq-pay-covered-note">
                                Good news — your wallet balance covered the full difference, so no card payment was needed.
                            </p>
                        )}
                        <button className="creq-btn creq-btn--primary" onClick={() => navigate('/client/bookings')}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="creq-modal-header creq-page-header">
                            <div>
                                <button
                                    type="button"
                                    className="creq-page-back"
                                    onClick={exitToBookings}
                                    disabled={submitting}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                                </button>
                                <h3>Edit Booking</h3>
                                <div className="creq-modal-sub">
                                    {original
                                        ? `${original.property_label} · ${original.type_label} · currently ${readableWhen(original.date, original.time)}`
                                        : 'Loading booking…'}
                                </div>
                            </div>
                        </div>

                        {/* HIDDEN, never unmounted, while the card form is
                            up: the half-made change is what is being paid for,
                            and re-mounting would reset it and re-quote. */}
                        <div className="creq-modal-body" style={payStep === 'card' ? { display: 'none' } : undefined}>
                            {loadError ? (
                                <div className="creq-error">{loadError}</div>
                            ) : blockedReason ? (
                                <div className="creq-hintline">{blockedReason}</div>
                            ) : !original || !form ? (
                                <div className="creq-price-panel creq-price-loading">
                                    <span className="creq-price-spinner" aria-hidden="true" />
                                    Loading booking…
                                </div>
                            ) : (
                                <>
                                    {/* ── Service ── */}
                                    <div className="creq-section-label">
                                        Service
                                        {changes.type && <span className="creq-changed-tag">changed</span>}
                                    </div>
                                    <div className="creq-type-grid">
                                        {SERVICE_TYPES.map((t) => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                className={`creq-segment creq-segment--type ${form.type_id === t.id ? 'is-active' : ''}`}
                                                onClick={() => set({ type_id: t.id })}
                                            >
                                                <span className="creq-service-icon" aria-hidden="true">
                                                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                                                </span>
                                                <span className="creq-service-text">
                                                    <strong>{t.name}</strong>
                                                    <span>{t.desc}</span>
                                                </span>
                                                {form.type_id === t.id && (
                                                    <FontAwesomeIcon icon={faCheckCircle} className="creq-service-check" aria-hidden="true" />
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {LINEN_HIRE_TYPES.includes(form.type_id) && (
                                        <div className="creq-linen-deposit-note">
                                            <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
                                            <span>{LINEN_HIRE_NOTE}</span>
                                        </div>
                                    )}

                                    {/* ── Schedule ──
                                        B6: the same mobile-style date strip as the
                                        create page. The original date is pre-selected
                                        (and auto-scrolled into view); time chips render
                                        because a date is always set here. */}
                                    <BookingDateStrip
                                        label={(
                                            <span className="creq-section-label creq-datestrip-label">
                                                New date &amp; time
                                                {changes.schedule && <span className="creq-changed-tag">changed</span>}
                                            </span>
                                        )}
                                        value={form.date}
                                        onSelect={(ds) => set({ date: ds })}
                                        disabled={submitting}
                                    />
                                    {isHourly && (
                                        <div className="creq-grid">
                                            <label className="creq-label">
                                                Duration
                                                {changes.duration && <span className="creq-changed-tag">changed</span>}
                                                <Select
                                                    options={DURATION_OPTIONS}
                                                    value={DURATION_OPTIONS.find((o) => o.value === form.duration_hours) || null}
                                                    onChange={(opt) => set({ duration_hours: opt ? opt.value : 2 })}
                                                    styles={coralSelectStyles}
                                                    theme={coralSelectTheme}
                                                    menuPortalTarget={document.body}
                                                    isSearchable={false}
                                                />
                                            </label>
                                        </div>
                                    )}

                                    {/* Time chips — hidden until a date exists (B6);
                                        an edit always has one, so this only guards the
                                        degenerate no-date parse. */}
                                    {!form.date ? (
                                        <div className="creq-timegroup-note creq-timegroup-note--pickdate">
                                            Pick a date to choose a start time.
                                        </div>
                                    ) : (
                                        <div className="creq-label creq-label--wide creq-timefield">
                                            <span className="creq-timefield-label">
                                                Start time
                                                {form.type_id === 1 && (
                                                    <span className="creq-timegroup-note" style={{ marginLeft: 8 }}>10% off from 16:00</span>
                                                )}
                                            </span>
                                            <div className="creq-timechips" role="radiogroup" aria-label="Start time">
                                                {ALL_TIME_SLOTS.map((t) => {
                                                    const active = form.time === t;
                                                    const offpeak = form.type_id === 1 && t >= OFFPEAK_FROM;
                                                    const tooSoon = isSlotTooSoon(t);
                                                    return (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            role="radio"
                                                            aria-checked={active}
                                                            disabled={tooSoon}
                                                            title={tooSoon ? 'This time has already passed' : undefined}
                                                            className={`creq-timechip${active ? ' is-active' : ''}${offpeak ? ' is-offpeak' : ''}`}
                                                            onClick={() => set({ time: t })}
                                                        >
                                                            {t}
                                                            {offpeak && <span className="creq-timechip-dot" aria-hidden="true" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {form.date && form.time && changes.schedule && (
                                        <div className="creq-dt-confirm">
                                            <span className="creq-dt-confirm-dot" aria-hidden="true" />
                                            Moving to <strong>{readableWhen(form.date, form.time)}</strong>
                                            {' '}(was {readableWhen(original.date, original.time)})
                                        </div>
                                    )}

                                    {form.date && form.time && changes.schedule && !quoteLoading
                                        && (notice?.messages?.last_minute || notice?.messages?.office_hours) && (
                                        <div className="creq-notice" role="note">
                                            {notice.messages.last_minute && (
                                                <p className="creq-notice__line creq-notice__line--lm">{notice.messages.last_minute}</p>
                                            )}
                                            {notice.messages.office_hours && (
                                                <p className="creq-notice__line">{notice.messages.office_hours}</p>
                                            )}
                                        </div>
                                    )}

                                    {changes.schedule && !timeValidForChange && (
                                        <div className="creq-hintline">
                                            Please pick a start time between 10:00 and 17:00 for the new schedule.
                                        </div>
                                    )}

                                    {isLmReschedule && (
                                        <div className="creq-cancel-note creq-cancel-note--warn">
                                            Your visit starts in <strong>less than 24 hours</strong>, so this is a
                                            last-minute change: the original visit stays as{' '}
                                            <strong>Last Minute Rescheduled</strong> with a fee of{' '}
                                            <strong>{money(quote?.penalty_fee_incl_vat ?? original.lm_fee_incl_vat)}</strong> (the rest is refunded
                                            to your wallet), and the new time is booked as a <strong>new visit at
                                            full price</strong>.
                                        </div>
                                    )}

                                    {isHourly && (
                                        <div className="creq-cleaners-row">
                                            <div className="creq-cleaners-label">
                                                How many cleaners?
                                                {changes.cleaners && <span className="creq-changed-tag">changed</span>}
                                            </div>
                                            <div className="creq-segmented creq-segmented--4">
                                                {[1, 2, 3, 4].map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        className={`creq-segment ${form.required_cleaners === n ? 'is-active' : ''}`}
                                                        onClick={() => set({ required_cleaners: n })}
                                                    >
                                                        <strong>{n}</strong>
                                                        <span>{n === 1 ? 'cleaner' : 'cleaners'}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Extra Linens ── */}
                                    {clientAddons.length > 0 && (
                                        <>
                                            <div className="creq-section-label">
                                                Extra Linens
                                                {changes.linens && <span className="creq-changed-tag">changed</span>}
                                            </div>
                                            <div className="creq-section-hint">
                                                Add or change extra linen-hire items for this booking — the price difference is charged/refunded on approval.
                                            </div>
                                            <div className="creq-linen">
                                                {(form.selected_addons || []).map((sa) => {
                                                    const item = clientAddons.find((a) => a.id === Number(sa.item_id));
                                                    if (!item) return null;
                                                    return (
                                                        <div className="creq-linen-row" key={sa.item_id}>
                                                            <span className="creq-linen-name">{item.name}</span>
                                                            <div className="creq-addon-stepper">
                                                                <button type="button" className="creq-addon-btn" aria-label={`Remove one ${item.name}`}
                                                                    disabled={submitting} onClick={() => setAddonQty(item.id, sa.quantity - 1)}>−</button>
                                                                <span className="creq-addon-qty">{sa.quantity}</span>
                                                                <button type="button" className="creq-addon-btn" aria-label={`Add one ${item.name}`}
                                                                    disabled={submitting || sa.quantity >= 20} onClick={() => setAddonQty(item.id, sa.quantity + 1)}>+</button>
                                                            </div>
                                                            <span className="creq-linen-line">{money(item.default_price * sa.quantity)}<span className="creq-addon-vat"> + VAT</span></span>
                                                            <button type="button" className="creq-linen-remove" aria-label={`Remove ${item.name}`}
                                                                disabled={submitting} onClick={() => setAddonQty(item.id, 0)}>×</button>
                                                        </div>
                                                    );
                                                })}
                                                <Select
                                                    className="creq-linen-select"
                                                    options={clientAddons
                                                        .filter((a) => addonQty(a.id) === 0)
                                                        .map((a) => ({ value: a.id, label: `${a.name} — ${money(a.default_price)} + VAT` }))}
                                                    value={null}
                                                    controlShouldRenderValue={false}
                                                    isSearchable
                                                    isDisabled={submitting || clientAddons.every((a) => addonQty(a.id) > 0)}
                                                    placeholder="＋ Add extra linen…"
                                                    noOptionsMessage={() => 'All extra linens added'}
                                                    onChange={(opt) => { if (opt) setAddonQty(opt.value, addonQty(opt.value) + 1); }}
                                                    styles={coralSelectStyles}
                                                    theme={coralSelectTheme}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* ── Tasks for this booking (above Notes — 2026-07-15) ── */}
                                    <div className="creq-section-label">
                                        Tasks for this cleaning
                                        {tasksChanged && <span className="creq-changed-tag">changed</span>}
                                    </div>
                                    <TaskListEditor
                                        tasks={tasks}
                                        onChange={setTasks}
                                        addLabel="Add a task for this cleaning"
                                        emptyHint="No extra tasks for this booking."
                                        disabled={submitting}
                                    />

                                    {/* ── Notes ── */}
                                    <div className="creq-section-label">
                                        Notes
                                        {changes.notes && <span className="creq-changed-tag">changed</span>}
                                    </div>
                                    <label className="creq-label creq-label--wide">
                                        <textarea
                                            maxLength={500}
                                            placeholder="Anything we should know for this booking…"
                                            value={form.notes}
                                            onChange={(e) => set({ notes: e.target.value })}
                                        />
                                    </label>

                                    {/* ── Price impact (server-quoted: TRUE net-charged basis) ── */}
                                    {priceTouched && (
                                        quoteLoading ? (
                                            <div className="creq-price-panel creq-price-loading">
                                                <span className="creq-price-spinner" aria-hidden="true" />
                                                Recalculating price…
                                            </div>
                                        ) : quoteFailed ? (
                                            <div className="creq-hintline">
                                                {quoteFailReason
                                                    || 'We couldn’t preview the new price — our team will confirm it during review before anything is charged.'}
                                            </div>
                                        ) : quote ? (
                                            <div className="creq-price-panel">
                                                <div className="creq-price-head">
                                                    <div>
                                                        <div className="creq-price-title">New Estimated Price</div>
                                                        <div className="creq-price-sub">Incl. VAT · final price confirmed on approval</div>
                                                    </div>
                                                    <div className="creq-price-total">
                                                        {money(quote.estimated_new_total)}
                                                    </div>
                                                </div>
                                                <div className="creq-price-breakdown">
                                                    <div className="creq-price-row">
                                                        <span>Current booking price</span>
                                                        <span>{money(quote.current_total)}</span>
                                                    </div>
                                                    {Math.abs(effectiveDelta) > 0.01 ? (
                                                        <div className="creq-price-row creq-price-row--strong">
                                                            <span>
                                                                {effectiveDelta > 0
                                                                    ? (isLmReschedule ? `Extra charge if approved (incl. ${money(quote.penalty_fee_incl_vat)} fee)` : 'Extra charge if approved')
                                                                    : 'Refunded to your wallet if approved'}
                                                            </span>
                                                            <span style={{ color: effectiveDelta > 0 ? '#b45309' : '#047857' }}>
                                                                {effectiveDelta > 0 ? '+' : '−'}{money(Math.abs(effectiveDelta))}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="creq-price-row">
                                                            <span>Price change</span>
                                                            <span>None</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Decrease / no change: the wallet summary lives here
                                                    (an increase gets the full payment selector below). */}
                                                {!needsSettlement && (
                                                    <div className="creq-wallet-rows">
                                                        <div className="creq-price-row">
                                                            <span>Wallet balance</span>
                                                            <span>{money(quote.wallet?.total)}</span>
                                                        </div>
                                                        {Number(quote.reserved_for_pending) > 0 && (
                                                            <div className="creq-price-row creq-wallet-reserved">
                                                                <span>Reserved · {quote.pending_bookings_count} other pending request{quote.pending_bookings_count === 1 ? '' : 's'}</span>
                                                                <span>−{money(quote.reserved_for_pending)}</span>
                                                            </div>
                                                        )}
                                                        {balanceAfterChange != null && (
                                                            <div className={`creq-price-row creq-wallet-after ${balanceAfterChange < 0 ? 'is-negative' : 'is-positive'}`}>
                                                                <span>Balance after this change (if approved)</span>
                                                                <span>{money(balanceAfterChange)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : null
                                    )}

                                    {/* ── Settlement (R3): ONE Pay-now path (2026-07-18).
                                        Was two option cards, "Use wallet" and "Pay now by card",
                                        for the same difference. Now one block, one toggle, and a
                                        derived rail — identical to the create flow so the two
                                        checkouts read the same. ── */}
                                    {needsSettlement && quote && !quoteLoading && (
                                        <>
                                            <div className="creq-section-label">Pay the difference</div>
                                            <div className="creq-pay-choice is-active">
                                                <div className="creq-pay-details creq-pay-details--wallet">
                                                    {payPlan.hasWalletMoney && (
                                                        <label className="creq-pay-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={useWalletBalance}
                                                                onChange={(e) => setUseWalletBalance(e.target.checked)}
                                                                disabled={submitting}
                                                            />
                                                            <span>Use my wallet balance ({money(availableRaw)})</span>
                                                        </label>
                                                    )}

                                                    <div className="creq-pay-breakdown">
                                                        <div className="creq-price-row">
                                                            <span>Price difference</span>
                                                            <span>{money(effectiveDelta)}</span>
                                                        </div>
                                                        {applyWallet && (
                                                            <div className="creq-price-row creq-wallet-reserved">
                                                                <span>Wallet covers</span>
                                                                <span>−{money(walletPortion)}</span>
                                                            </div>
                                                        )}
                                                        <div className="creq-price-row creq-price-row--strong">
                                                            <span>{payPlan.needsCard ? 'To pay by card' : 'To pay now'}</span>
                                                            <span>{money(cardPortion)}</span>
                                                        </div>
                                                        {/* NO "Wallet balance after" row (owner
                                                            2026-07-19, twice — "Balance after still
                                                            visible on web view for booking request").
                                                            The breakdown is his formula and nothing
                                                            else: additions, one wallet deduction, one
                                                            to-pay line. Do not re-add balance rows. */}
                                                    </div>

                                                    {/* The card-leg branch of this note carried the
                                                        secure-checkout sentence until 2026-07-19 (owner:
                                                        the reassurance's one sanctioned home is
                                                        PayNowPanel, which this page does not host) —
                                                        a card leg now renders no note at all. */}
                                                    {!payPlan.needsCard && (
                                                        <div className="creq-pay-note">
                                                            Your wallet balance covers this change — submitting will simply use your wallet, no card payment needed.
                                                        </div>
                                                    )}

                                                    {cardPortion > 0 && !coveredByPending && (
                                                        <div className="creq-pay-cardpicker">
                                                            <SavedCardPicker
                                                                cards={savedCards}
                                                                value={cardSelection}
                                                                onChange={setCardSelection}
                                                                allowSaveNew={false}
                                                                disabled={submitting || savedCardBusy}
                                                                /* The security code belongs in the row of
                                                                   the card it protects. */
                                                                cvvValue={savedCardActive ? savedCvv : null}
                                                                onCvvChange={savedCardActive ? setSavedCvv : null}
                                                                newCardSub={embeddedCardRail
                                                                    ? 'Continue to input card details or select digital payments.'
                                                                    : null}
                                                            />
                                                            {/* Pay the difference with the stored card
                                                                here — no second step, no redirect. The
                                                                button is portalled into this page's
                                                                action row. */}
                                                            {savedCardActive && (
                                                                <SavedCardPay
                                                                    card={selectedSavedCard}
                                                                    compact
                                                                    cvv={savedCvv}
                                                                    onCvvChange={setSavedCvv}
                                                                    onBusyChange={setSavedCardBusy}
                                                                    actionSlot={paySlot}
                                                                    payLabel={`Pay ${money(cardPortion)}`}
                                                                    disabled={submitting || !canSubmit}
                                                                    charge={chargeSavedCardNow}
                                                                    onPaid={onCardPaid}
                                                                    onFailed={() => { /* the row states its own outcome */ }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Quiet nudge — the wallet top-up path, kept as plain
                                                        text with an arrow rather than a competing rail
                                                        (2026-07-19). Wording and styling match
                                                        PayNowPanel's .pnp__nudge, the create page, and
                                                        mobile's <TopUpNudge>; the old "Earn bonuses"-first
                                                        variant pitched the wallet ahead of paying. */}
                                                    {payPlan.needsCard && !coveredByPending && (
                                                        <button
                                                            type="button"
                                                            className="creq-pay-topupLink"
                                                            onClick={() => openDeposit()}
                                                            disabled={submitting}
                                                        >
                                                            Or top up your wallet and earn bonuses →
                                                        </button>
                                                    )}

                                                    {/* A SECOND standalone wallet-math block ("Wallet
                                                        balance / Reserved / This change / Balance after
                                                        this change…") rendered here until 2026-07-19.
                                                        DELETED (item 13 — the same duplication the owner
                                                        screenshotted on mobile): the pay breakdown above
                                                        already states the difference, the wallet portion,
                                                        the card charge and the balance after off the ONE
                                                        derived plan. One fact, one rendering. Do not
                                                        reintroduce a parallel arithmetic path. (The
                                                        decrease/no-change summary further up is not a
                                                        duplicate — no payment renders there.) */}

                                                    {coveredByPending && (
                                                        <div className="creq-wallet-covered">
                                                            <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                                                            <span>
                                                                Covered by your pending top-up{Number(quote.pending_deposits_all_count) === 1 ? '' : 's'} — this
                                                                change will be applied once your top-up is approved.
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {!!payNotice && <div className="creq-notice">{payNotice}</div>}

                                    {/* ZERO FLOOR (P3) — shortfall in money plus
                                        the resolving actions, never a dead error. */}
                                    {walletFloor && (
                                        <WalletShortfallPanel
                                            heading="Your wallet doesn’t cover this change yet"
                                            cost={walletFloor.required != null
                                                ? walletFloor.required
                                                : Math.max(0, Number(effectiveDelta || 0))}
                                            available={walletFloor.available != null ? walletFloor.available : availableRaw}
                                            totalBalance={walletFloor.totalBalance}
                                            committed={walletFloor.committed}
                                            amountOwed={walletFloor.amountOwed}
                                            shortfall={walletFloor.shortfall}
                                            disabled={submitting}
                                            onPayByCard={() => {
                                                setWalletFloor(null);
                                                setUseWalletBalance(true);
                                                submitCardCheckout();
                                            }}
                                            onTopUp={() => { setWalletFloor(null); openDeposit(); }}
                                        />
                                    )}

                                    {!!error && <div className="creq-error">{error}</div>}
                                </>
                            )}
                        </div>

                        {/* STEP 2 — only the payment: what the change costs,
                            and the gateway's own fields. */}
                        {payStep === 'card' && (
                            <div className="creq-modal-body creq-cardStep">
                                <div className="creq-cardStep__summary">
                                    <div className="creq-cardStep__row">
                                        <span>Extra charge for this change</span>
                                        <span>{money(effectiveDelta)}</span>
                                    </div>
                                    {walletPortion > 0.005 && (
                                        <div className="creq-cardStep__row creq-cardStep__row--wallet">
                                            <span>From your wallet</span>
                                            <span>−{money(walletPortion)}</span>
                                        </div>
                                    )}
                                    <div className="creq-cardStep__total">
                                        <span>To pay by card</span>
                                        <strong>{money(cardPortion)}</strong>
                                    </div>
                                </div>
                                <div className="creq-cardStep__note">
                                    Your booking stays as originally scheduled until our team approves the change.
                                </div>
                                {!!error && <div className="creq-error">{error}</div>}
                                <HostedCardPanel
                                    key={`edit-${Number(cardPortion || 0).toFixed(2)}`}
                                    payLabel={`Pay ${money(cardPortion)}`}
                                    amountAed={Number(cardPortion || 0)}
                                    disabled={submitting || !canSubmit}
                                    startPayment={startHostedPayment}
                                    onUnavailable={() => { setHostedRail('off'); setPayStep('form'); }}
                                    onPaid={onCardPaid}
                                    onFailed={() => { /* the panel states its own outcome and re-arms */ }}
                                    actionSlot={paySlot}
                                >
                                    <label className="hcard-save">
                                        <input
                                            type="checkbox"
                                            checked={saveNewCard}
                                            onChange={(e) => setSaveNewCard(e.target.checked)}
                                        />
                                        <span>Save this card for future payments</span>
                                    </label>
                                    {saveNewCard && (
                                        <div className="hcard-consent hcard-consent--readonly">
                                            <span className="hcard-consent-text">
                                                <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                                            </span>
                                        </div>
                                    )}
                                </HostedCardPanel>
                            </div>
                        )}

                        {/* LAST-MINUTE TICK. One place, directly above the one
                            action row every way of paying ends in. */}
                        {ackRequired && (
                            <label className="creq-lmack">
                                <input
                                    type="checkbox"
                                    checked={lmAck}
                                    disabled={submitting}
                                    onChange={(e) => { setLmAck(e.target.checked); if (e.target.checked) setError(''); }}
                                />
                                <span>{notice.messages.ack_label}</span>
                            </label>
                        )}

                        <div className="creq-modal-actions creq-modal-actions--split">
                            <div className="creq-actions-left">
                                {/* Not beside a live payment: cancelling the
                                    booking is the opposite of paying for its
                                    change, and it is one tap from Back. */}
                                {!blockedReason && payStep !== 'card' && (
                                    <button
                                        type="button"
                                        className="creq-cancel-instead"
                                        onClick={() => { if (!submitting) setShowCancelModal(true); }}
                                        disabled={submitting}
                                    >
                                        Cancel this booking instead
                                    </button>
                                )}
                            </div>
                            <div className="creq-actions-right">
                                <button
                                    className="creq-btn creq-btn--ghost"
                                    onClick={payStep === 'card'
                                        ? () => { releaseStagedCheckout(); setPayStep('form'); setError(''); }
                                        : exitToBookings}
                                    disabled={submitting}
                                >
                                    {payStep === 'card' ? 'Back' : 'Close'}
                                </button>
                                {/* The embedded rails portal their Pay button in
                                    here, so this row holds the same two controls
                                    whichever way the client is paying. */}
                                <div className="creq-paySlot" ref={setPaySlot} />
                                {!embeddedPayOwnsButton && (
                                <button
                                    className="creq-btn creq-btn--primary"
                                    onClick={handleSubmitClick}
                                    disabled={!canSubmit}
                                    title={!hasChanges ? 'Change something first' : undefined}
                                >
                                    {/* ONE primary button, labelled with the ACTUAL outcome of
                                        the derived rail. */}
                                    {submitting ? 'Submitting…'
                                        : (needsSettlement && cardPortion > 0 && !coveredByPending)
                                            ? `Pay ${money(cardPortion)} by Card`
                                            : 'Submit Changes'}
                                </button>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* "Cancel this booking instead" — the cancel flow stays a modal
                    (only the request/edit forms became pages); submitting a
                    cancellation returns the client to My Bookings. */}
                {showCancelModal && (
                    <RequestBookingCancelModal
                        bookingId={bookingId}
                        onClose={() => {
                            setShowCancelModal(false);
                            if (cancelSubmittedRef.current) navigate('/client/bookings');
                        }}
                        onSubmitted={() => { cancelSubmittedRef.current = true; }}
                    />
                )}

                {/* The top-up form allows the card rail (owner 2026-07-19 fix
                    kept from the modal era): a card top-up redirects to the
                    hosted paypage, and the half-made change survives because
                    openDeposit snapshots it to localStorage (bookingEditDraft)
                    and ClientDepositForm takes a returnPath — the paypage
                    returns to /client/payment-return/bookings, which offers to
                    reopen THIS PAGE with the change intact (?resumeDraft=1).
                    No money behaviour changes: it is the same top-up checkout
                    the wallet page has always used, and the change request is
                    still only submitted by an explicit submit afterwards. */}
                {showDeposit && (
                    <ClientDepositForm
                        walletClientId={quote?.wallet_client_id || null}
                        walletLabel={quote?.wallet_label || null}
                        initialAmount={shortfall || serverShortfall}
                        onClose={() => setShowDeposit(false)}
                        onSuccess={handleDepositSuccess}
                        allowCard
                        returnPath="/client/payment-return/bookings"
                    />
                )}
            </div>
        </div>
    );
}
